import { useEffect, useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Trophy, Star, Sparkles, X } from 'lucide-react'
import { Button, Card, CardContent } from '@/components/ui'
import type { Badge } from '@/services/badgeService'
import type { Mission } from '@/types/database'

export interface MissionCompleteData {
  mission: Mission
  wordCount: number
  newBadges: Badge[]
  totalMissionsCompleted: number
}

interface MissionCompleteModalProps {
  isOpen: boolean
  data: MissionCompleteData | null
  onClose: () => void
}

const CONFETTI_COLORS = [
  'var(--color-kids-primary)',
  'var(--color-kids-secondary)',
  'var(--color-kids-accent)',
  'var(--color-kids-warning)',
  'var(--color-kids-success)',
]

interface ParticleData {
  id: number
  delay: number
  x: number
  style: {
    color: string
    size: number
    rotation: number
    borderRadius: string
    xOffset: number
    duration: number
  }
}

// Pre-generate confetti particles at module level (outside render)
function generateConfettiParticles(): ParticleData[] {
  return Array.from({ length: 50 }, (_, i) => ({
    id: i,
    delay: Math.random() * 0.5,
    x: Math.random() * 100,
    style: {
      color: CONFETTI_COLORS[Math.floor(Math.random() * CONFETTI_COLORS.length)],
      size: 8 + Math.random() * 8,
      rotation: Math.random() * 360,
      borderRadius: Math.random() > 0.5 ? '50%' : '2px',
      xOffset: (Math.random() - 0.5) * 200,
      duration: 2 + Math.random() * 2,
    },
  }))
}

// Confetti particle component
function ConfettiParticle({ particle }: { particle: ParticleData }) {
  const { delay, x, style } = particle
  return (
    <motion.div
      className="absolute pointer-events-none"
      style={{
        width: style.size,
        height: style.size,
        backgroundColor: style.color,
        borderRadius: style.borderRadius,
        left: `${x}%`,
        top: -20,
      }}
      initial={{ y: -20, opacity: 1, rotate: style.rotation }}
      animate={{
        y: '100vh',
        opacity: [1, 1, 0],
        rotate: style.rotation + 720,
        x: style.xOffset,
      }}
      transition={{
        duration: style.duration,
        delay: delay,
        ease: 'easeIn',
      }}
    />
  )
}

// Confetti burst - generates new particles each time it mounts
function Confetti() {
  // Generate particles once when component mounts using useState initializer
  const [particles] = useState<ParticleData[]>(generateConfettiParticles)

  return (
    <div className="fixed inset-0 overflow-hidden pointer-events-none z-50">
      {particles.map((p) => (
        <ConfettiParticle key={p.id} particle={p} />
      ))}
    </div>
  )
}

const CELEBRATION_MESSAGES = [
  "You're a writing superstar!",
  "Great job today!",
  "Your story is coming along wonderfully!",
  "Keep up the awesome work!",
  "You're becoming a better writer every day!",
]

// Celebration messages based on performance
function getCelebrationMessage(wordCount: number, totalMissions: number, randomIndex: number): string {
  if (totalMissions === 1) return "You completed your first mission!"
  if (wordCount >= 200) return "Wow! You wrote so much today!"
  if (totalMissions % 10 === 0) return `Amazing! ${totalMissions} missions completed!`
  return CELEBRATION_MESSAGES[randomIndex % CELEBRATION_MESSAGES.length]
}

// Generate random index outside render
function generateRandomMessageIndex(): number {
  return Math.floor(Math.random() * CELEBRATION_MESSAGES.length)
}

export function MissionCompleteModal({ isOpen, data, onClose }: MissionCompleteModalProps) {
  // Track when confetti should be hidden (after timeout)
  const [confettiHidden, setConfettiHidden] = useState(false)
  // Derive showConfetti from isOpen and timeout state
  const showConfetti = isOpen && !confettiHidden

  // Random message index - generated once per data change using useState initializer
  const [randomMessageIndex, setRandomMessageIndex] = useState(generateRandomMessageIndex)

  // Reset random index when data changes (new mission completion)
  useEffect(() => {
    if (data) {
      const timer = setTimeout(() => setRandomMessageIndex(generateRandomMessageIndex()), 0)
      return () => clearTimeout(timer)
    }
  }, [data])

  useEffect(() => {
    if (!isOpen) {
      // Reset for next open - use microtask to avoid sync setState warning
      const resetTimer = setTimeout(() => setConfettiHidden(false), 0)
      return () => clearTimeout(resetTimer)
    }

    // Hide confetti after animation completes
    const timer = setTimeout(() => setConfettiHidden(true), 4000)
    return () => clearTimeout(timer)
  }, [isOpen])

  if (!data) return null

  const celebrationMessage = getCelebrationMessage(data.wordCount, data.totalMissionsCompleted, randomMessageIndex)

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Confetti */}
          {showConfetti && <Confetti />}

          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-40"
            onClick={onClose}
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.8, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.8, y: 20 }}
            transition={{ type: 'spring', duration: 0.5 }}
            className="fixed inset-0 flex items-center justify-center z-50 p-4"
          >
            <Card className="max-w-md w-full relative overflow-visible">
              {/* Close button */}
              <button
                onClick={onClose}
                className="absolute -top-2 -right-2 w-8 h-8 bg-white rounded-full shadow-lg flex items-center justify-center hover:bg-gray-100 transition-colors z-10"
              >
                <X className="w-4 h-4 text-gray-500" />
              </button>

              <CardContent className="p-8 text-center">
                {/* Trophy animation */}
                <motion.div
                  initial={{ scale: 0, rotate: -20 }}
                  animate={{ scale: 1, rotate: 0 }}
                  transition={{ type: 'spring', delay: 0.2, duration: 0.6 }}
                  className="mb-6"
                >
                  <div className="w-24 h-24 mx-auto bg-gradient-to-br from-[var(--color-kids-warning)] to-[var(--color-kids-accent)] rounded-full flex items-center justify-center shadow-lg">
                    <Trophy className="w-12 h-12 text-white" />
                  </div>
                </motion.div>

                {/* Title */}
                <motion.h2
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                  className="font-display text-2xl font-bold text-[var(--color-kids-text)] mb-2"
                >
                  Mission Complete!
                </motion.h2>

                {/* Celebration message */}
                <motion.p
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 }}
                  className="text-[var(--color-kids-text-muted)] mb-6"
                >
                  {celebrationMessage}
                </motion.p>

                {/* Stats */}
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5 }}
                  className="grid grid-cols-2 gap-4 mb-6"
                >
                  <div className="bg-[var(--color-kids-surface-alt)] rounded-[var(--radius-kid)] p-4">
                    <div className="flex items-center justify-center gap-2 mb-1">
                      <Sparkles className="w-4 h-4 text-[var(--color-kids-primary)]" />
                      <span className="text-sm text-[var(--color-kids-text-muted)]">Words Written</span>
                    </div>
                    <p className="font-display text-2xl font-bold text-[var(--color-kids-primary)]">
                      {data.wordCount.toLocaleString()}
                    </p>
                  </div>
                  <div className="bg-[var(--color-kids-surface-alt)] rounded-[var(--radius-kid)] p-4">
                    <div className="flex items-center justify-center gap-2 mb-1">
                      <Star className="w-4 h-4 text-[var(--color-kids-warning)]" />
                      <span className="text-sm text-[var(--color-kids-text-muted)]">Missions Done</span>
                    </div>
                    <p className="font-display text-2xl font-bold text-[var(--color-kids-warning)]">
                      {data.totalMissionsCompleted}
                    </p>
                  </div>
                </motion.div>

                {/* New badges */}
                {data.newBadges.length > 0 && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.6 }}
                    className="mb-6"
                  >
                    <p className="text-xs text-[var(--color-kids-text-muted)] uppercase tracking-wide mb-3">
                      New Badge{data.newBadges.length > 1 ? 's' : ''} Earned!
                    </p>
                    <div className="flex flex-wrap justify-center gap-3">
                      {data.newBadges.map((badge, index) => (
                        <motion.div
                          key={badge.id}
                          initial={{ scale: 0, rotate: -180 }}
                          animate={{ scale: 1, rotate: 0 }}
                          transition={{
                            type: 'spring',
                            delay: 0.7 + index * 0.1,
                            duration: 0.6,
                          }}
                          className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-[var(--color-kids-warning)]/20 to-[var(--color-kids-accent)]/20 border-2 border-[var(--color-kids-warning)] rounded-full"
                        >
                          <span className="text-2xl">{badge.emoji}</span>
                          <span className="font-semibold text-[var(--color-kids-text)]">
                            {badge.name}
                          </span>
                        </motion.div>
                      ))}
                    </div>
                  </motion.div>
                )}

                {/* Continue button */}
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.8 }}
                >
                  <Button onClick={onClose} size="lg" className="w-full">
                    Continue to Dashboard
                  </Button>
                </motion.div>
              </CardContent>
            </Card>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  )
}
